import { Controller } from "@hotwired/stimulus";
import {
  getNextDatapointUrl,
  resolveBoundRouteForController,
} from "../../db/sanitationRoutes.js";
import { checkOnlineStatus } from "../../onlineStatus.js";
import {
  navigateToRouteDocument,
  recoverFromBlockedRouteNavigation,
  recoverFromBoundRouteResolutionFailure,
} from "../../routeNavigation.js";
import { gpsMetadataForPosition } from "../route_datapoints/form_controller.js";
import {
  isTrailRequestDue,
  recordTrailPosition,
  TRAIL_MAX_INTERVAL_MS,
} from "../../db/backgroundTrail.js";

// Connects to data-controller="sanitation-forms--open-ended-checkpoint"
export default class extends Controller {
  static targets = [
    "continueButton",
    "completeButton",
    "loadingSpinnerContinue",
    "loadingSpinnerComplete",
    "recoveryMessage",
    "recoveryMessageText",
  ];

  static values = {
    routeLauncherPath: String,
    savedOfflineBlockedMessage: String,
    missingActiveRouteMessage: String,
    farmId: Number,
    sanitationFormId: Number,
  };

  static geolocationOptions = {
    enableHighAccuracy: true,
    maximumAge: 40000,
    timeout: 40000,
  };

  async connect() {
    this.disableButtons();
    checkOnlineStatus();

    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "open_ended_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      this.sanitationRoute = resolution.route;

      if (!navigator.geolocation) {
        alert(
          "Este dispositivo no soporta la geolocalización. No es posible continuar el recorrido",
        );
        this.disableButtons();
        return;
      }
      this.startBackgroundTracking();
      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    } catch (error) {
      alert(`Error al cargar checkpoint: ${error.message}`);
      this.disableButtons();
    }
  }

  disconnect() {
    if (this.geoInterval) {
      clearInterval(this.geoInterval);
      this.geoInterval = null;
    }
  }

  handleResolutionFailure(resolution) {
    return recoverFromBoundRouteResolutionFailure(this, resolution, {
      message: this.missingActiveRouteMessageValue,
      disableControls: () => this.disableControlsForRecovery(),
    });
  }

  disableButtons() {
    if (this.hasContinueButtonTarget) this.continueButtonTarget.disabled = true;
    if (this.hasCompleteButtonTarget) this.completeButtonTarget.disabled = true;
  }

  disableControlsForRecovery() {
    this.setLoadingState("continue", false);
    this.setLoadingState("complete", false);
    this.disableButtons();
  }

  setLoadingState(buttonType, isLoading) {
    let button, spinner;
    if (buttonType === "continue") {
      if (
        !this.hasContinueButtonTarget ||
        !this.hasLoadingSpinnerContinueTarget
      )
        return;
      button = this.continueButtonTarget;
      spinner = this.loadingSpinnerContinueTarget;
    } else {
      if (
        !this.hasCompleteButtonTarget ||
        !this.hasLoadingSpinnerCompleteTarget
      )
        return;
      button = this.completeButtonTarget;
      spinner = this.loadingSpinnerCompleteTarget;
    }
    button.disabled = isLoading;
    spinner.classList.toggle("hidden", !isLoading);
    const buttonTextSpan = button.querySelector("span:not(.loading)");
    if (buttonTextSpan) buttonTextSpan.classList.toggle("invisible", isLoading);
  }

  areButtonsDisabled() {
    const continueDisabled = this.hasContinueButtonTarget
      ? this.continueButtonTarget.disabled
      : true;
    const completeDisabled = this.hasCompleteButtonTarget
      ? this.completeButtonTarget.disabled
      : true;
    return continueDisabled || completeDisabled;
  }

  startBackgroundTracking() {
    if (!this.sanitationRoute) return;
    // Gate on the persisted route state BEFORE requesting any GPS fix, and
    // take a single fix (maximumAge lets the browser reuse a recent one) —
    // maybeRecordRouteLocation applies the distance/time/accuracy gates.
    const trackPosition = () => {
      if (!isTrailRequestDue(this.sanitationRoute)) return;

      navigator.geolocation.getCurrentPosition(
        (position) => this.recordTrailPosition(position),
        (error) => {
          console.error("Background tracking error:", error);
        },
        this.constructor.geolocationOptions,
      );
    };
    trackPosition();
    this.geoInterval = setInterval(trackPosition, TRAIL_MAX_INTERVAL_MS);
  }

  recordTrailPosition(position) {
    return recordTrailPosition(this.sanitationRoute, position, () =>
      gpsMetadataForPosition(position, { source: "current" }),
    );
  }

  async continueToNextDatapoint(event) {
    event.preventDefault();
    if (this.areButtonsDisabled()) {
      return;
    }

    this.setLoadingState("continue", true);
    this.setLoadingState("complete", true);

    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "open_ended_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      const nextUrl = await getNextDatapointUrl(resolution.route.id, {
        intent: "next_step",
      });

      await this.navigateAfterLocalStateChange(nextUrl);
    } catch (error) {
      alert(`Error al continuar el recorrido: ${error.message}`);
      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    }
  }

  async completeSanitationRoute(event) {
    event.preventDefault();
    if (this.areButtonsDisabled()) return;

    this.setLoadingState("continue", true);
    this.setLoadingState("complete", true);

    try {
      const resolution = await resolveBoundRouteForController(
        this,
        "open_ended_checkpoint",
      );
      if (!resolution.ok) {
        return await this.handleResolutionFailure(resolution);
      }

      const nextUrl = await getNextDatapointUrl(resolution.route.id, {
        intent: "complete_route",
      });

      await this.navigateAfterLocalStateChange(nextUrl);
    } catch (error) {
      console.error(
        "Error completing route from open_ended_checkpoint:",
        error,
      );
      alert(`Error al completar el recorrido: ${error.message}`);
      this.setLoadingState("continue", false);
      this.setLoadingState("complete", false);
    }
  }

  async navigateAfterLocalStateChange(nextUrl) {
    const result = await navigateToRouteDocument(nextUrl);
    if (result.allowed) return result;

    await recoverFromBlockedRouteNavigation(this, result, {
      warnMessage:
        "Open-ended checkpoint advanced but next screen is missing offline",
      disableControls: () => this.disableControlsForRecovery(),
    });
    return result;
  }
}
